import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import { DayBarChart } from "~/components/day-bar-chart";
import {
  type DayCount,
  fillDateGaps,
  chartDateRange,
} from "~/lib/shared/day-chart";
import { orange } from "~/lib/ui";

export const getUsersByDay = createServerFn({ method: "GET" }).handler(
  async (): Promise<Array<DayCount>> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { users } = await import("~/../drizzle/schema");
    const { sql, and, gte, lt, isNotNull } = await import("drizzle-orm");

    const { rangeStart, rangeEnd } = chartDateRange(30, 0);
    const db = await getDBWithInit();

    const rows = await db
      .select({
        date: sql<string>`DATE(${users.createdAt})`.as("date"),
        count: sql<number>`COUNT(*)::int`.as("count"),
      })
      .from(users)
      .where(
        and(
          gte(users.createdAt, rangeStart),
          lt(users.createdAt, rangeEnd),
          isNotNull(users.clerkUserId),
        ),
      )
      .groupBy(sql`DATE(${users.createdAt})`)
      .orderBy(sql`DATE(${users.createdAt})`);

    return fillDateGaps(rows, {
      daysBefore: 30,
      daysAfter: 0,
      pastColor: orange[500],
      futureColor: orange[300],
    });
  },
);

function UsersChartPage() {
  const { data } = Route.useLoaderData();
  return <DayBarChart data={data} noun="Users" rangeLabel="last 30 days" />;
}

export const Route = createFileRoute("/embed/users-chart/")({
  head: () => ({
    title: "Users Chart - Dream Coach Match",
    meta: [
      {
        name: "description",
        content: "Embedded chart showing new user signups over time",
      },
    ],
  }),
  loader: async () => {
    const data = await getUsersByDay();
    return { data };
  },
  component: UsersChartPage,
});
